import { gql, useQuery } from "@apollo/client";
import { faPencil } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CircularProgress,
  IconButton,
  makeStyles,
  TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useAuthContext } from "auth/context";
import { usePollingIntervalConfig } from "client/utils/polling";
import clsx from "clsx";
import { GetEmailTemplatesForPicker } from "generated/schemaTypes";
import _ from "lodash";
import useAnalytics from "react-hooks/useAnalytics";
import { ResourceCustomTheme } from "styles/config";
import { PermissionEnum } from "utils/permissions";

const GET_EMAIL_TEMPLATES_FOR_PICKER = gql`
  query GetEmailTemplatesForPicker {
    currentOrganization {
      id
      emailTemplates(where: { status: ACTIVE }, orderBy: [{ name: asc }]) {
        id
        name
        archivedAt
      }
    }
  }
`;

const CreateNewOption = "create-new";
const LoadingOption = "loading";

type EmailTemplate = {
  id: string;
  name: string;
  archivedAt: Date | null;
};

const useStyles = makeStyles((_theme: ResourceCustomTheme) => ({
  root: {
    width: "100%",
    display: "flex",
  },
  select: {
    flexGrow: 1,
  },
  editBtn: {
    flexShrink: 0,
  },
  link: {
    textDecoration: "underline",
  },
}));

type EmailTemplatePickerProps = {
  defaultEmailTemplate?: EmailTemplate;
  label?: string;
  placeholder?: string;
  value?: string;
  name?: string;
  onChange(emailTemplateId: string | null): void;
  error?: boolean;
  disableClearable: boolean;
  className?: string;
  allowEdit?: boolean;
};

function EmailTemplatePicker({
  defaultEmailTemplate,
  value = "",
  placeholder,
  label = "Email Template",
  onChange,
  error = false,
  disableClearable,
  name,
  className = "",
  allowEdit = true,
}: EmailTemplatePickerProps) {
  const { checkRolePermissions } = useAuthContext();
  const hasWritePerms = checkRolePermissions(PermissionEnum.TEMPLATE_WRITE);
  const classes = useStyles();
  const analytics = useAnalytics();

  const { templatePicker: pollInterval } = usePollingIntervalConfig();
  const { data: emailTemplateData, loading } =
    useQuery<GetEmailTemplatesForPicker>(GET_EMAIL_TEMPLATES_FOR_PICKER, {
      pollInterval,
    });
  const emailTemplates =
    emailTemplateData?.currentOrganization?.emailTemplates ?? [];
  const selectedEmailTemplate =
    _.find(emailTemplates, { id: value }) || defaultEmailTemplate;
  return (
    <div className={clsx(classes.root, className)}>
      <Autocomplete<EmailTemplate, false, typeof disableClearable>
        value={
          disableClearable && !selectedEmailTemplate
            ? // When disableClearable is true, we cannot pass null or undefined,
              // so we need to pass a fake Loading option until our data loads in
              {
                id: LoadingOption,
                name: "Loading…",
                archivedAt: null,
              }
            : selectedEmailTemplate ?? null
        }
        className={classes.select}
        disableClearable={disableClearable}
        loading={loading}
        options={[
          ...(emailTemplates ?? []),
          ...(hasWritePerms
            ? [
                {
                  id: CreateNewOption,
                  name: "Create New Email Template",
                  archivedAt: null,
                },
              ]
            : []),
        ]}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            autoComplete="off"
            error={error}
            // A label that starts with Email is causing chrome to auto-show emails over top of our options
            label={label ?? "Template"}
            placeholder={placeholder ?? "Select an Email Template"}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ChipProps={{
          color: "primary",
        }}
        getOptionLabel={(option) =>
          `${option.name}${option.archivedAt ? ` (archived)` : ""}`
        }
        onChange={(_event, newValue) => {
          if (newValue?.id === CreateNewOption) {
            window.open("/templates/emails/?new=true", "_blank");
            analytics.track("Create New Email Template Clicked");
          } else if (newValue?.id !== LoadingOption) {
            onChange(newValue?.id ?? null);
          }
        }}
      />
      {allowEdit && hasWritePerms && !!value && (
        <IconButton
          className={classes.editBtn}
          onClick={() => {
            window.open(`/templates/emails/?edit=${value}`);
          }}
        >
          <FontAwesomeIcon icon={faPencil} size="sm" />
        </IconButton>
      )}
    </div>
  );
}

export default EmailTemplatePicker;
